<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px; font-size: small;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="card-title">Gestión de Solicitudes de Contratación</h5>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <input 
                    v-model="filters.search" 
                    type="text" 
                    class="form-control" 
                    placeholder="Buscar por código u objeto..."
                    @keyup.enter="handleSearch"
                  >
                  <select v-model="filters.estado" class="form-select" style="max-width: 180px;">
                    <option value="">Todos los estados</option>
                    <option v-for="estado in estados" :key="estado" :value="estado">
                      {{ formatEstado(estado) }}
                    </option>
                  </select>
                  <select v-model="filters.tipo_contratacion" class="form-select" style="max-width: 180px;">
                    <option value="">Todos los tipos</option>
                    <option value="BIENES">Bienes</option>
                    <option value="SERVICIOS">Servicios</option>
                    <option value="CONSULTORIA_LINEA">Consultoría Línea</option>
                    <option value="CONSULTORIA_PRODUCTO">Consultoría Producto</option>
                  </select>
                  <button class="btn btn-outline-secondary" type="button" @click="handleSearch">
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-4 text-end">
              <BButton @click="openModal()" variant="primary" class="btn-round me-2">
                <i class="fa fa-plus me-1"></i> Nueva Solicitud
              </BButton>
              <BButton variant="danger" class="btn-round" @click="exportPDF">
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        
        <div class="card-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
            <p class="mt-2">Cargando solicitudes...</p>
          </div>
          
          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>Código</th>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Objeto</th>
                    <th>Monto</th>
                    <th>Unidad</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                   </tr>
                </thead>
                <tbody>
                  <tr v-for="solicitud in solicitudes.data" :key="solicitud.id">
                    <td><strong>{{ solicitud.codigo }}</strong></td>
                    <td>{{ formatDate(solicitud.fecha_registro) }}</td>
                    <td><BBadge :variant="getTipoVariant(solicitud.tipo_contratacion)">{{ formatTipo(solicitud.tipo_contratacion) }}</BBadge></td>
                    <td><div style="max-width: 250px; white-space: normal;">{{ truncate(solicitud.objeto, 60) }}</div></td>
                    <td class="text-end"><strong>Bs. {{ formatNumber(solicitud.monto_estimado) }}</strong></td>
                    <td>{{ solicitud.unidad_solicitante?.nombre_unidad || 'N/A' }}</td>
                    <td><BBadge :variant="getEstadoVariant(solicitud.estado)">{{ formatEstado(solicitud.estado) }}</BBadge></td>
                    <td>
                      <div class="btn-group">
                        <BButton @click="showDetails(solicitud.id)" variant="info" size="sm" class="me-1" title="Ver detalles">
                          <i class="fa fa-eye"></i>
                        </BButton>
                        <!-- Editar (abre modal) -->
                        <BButton v-if="canEdit(solicitud)" @click="openEditModal(solicitud)" variant="warning" size="sm" class="me-1" title="Editar">
                          <i class="fa fa-edit"></i>
                        </BButton>
                        
                        <BButton v-if="canSend(solicitud)" @click="enviarAutorizacion(solicitud)" variant="success" size="sm" class="me-1" title="Enviar a Autorización">
                          <i class="fa fa-paper-plane"></i>
                        </BButton>
                        <BButton v-if="canDelete(solicitud)" @click="confirmDelete(solicitud)" variant="danger" size="sm" title="Eliminar">
                          <i class="fa fa-trash"></i>
                        </BButton>
                        <BButton variant="danger" v-if="!canDelete(solicitud)" size="sm" @click="generarPdfFus(solicitud)" :disabled="generandoPdf" title="Generar Formulario Único de Solicitud (FUS)">
                          <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
                          <i v-else class="fa fa-file-pdf"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="row mt-3" v-if="solicitudes.total > 0">
              <div class="col-md-12">
                <BPagination 
                  v-model="pagination.current_page" 
                  :total-rows="solicitudes.total" 
                  :per-page="pagination.per_page" 
                  @input="handlePageChange" 
                  align="center"
                />
              </div>
            </div>
            
            <div v-if="solicitudes.data?.length === 0" class="alert alert-info text-center">
              <i class="fa fa-info-circle"></i> No hay solicitudes registradas
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR SOLICITUD (sin detalles) -->
    <BModal v-model="modalVisible" title="Nueva Solicitud de Contratación" size="lg" centered hide-footer>
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Unidad Solicitante *</label>
              <select v-model="form.id_unidad_solicitante" class="form-select" :class="{ 'is-invalid': errors.id_unidad_solicitante }" required>
                <option value="">Seleccionar unidad</option>
                <option v-for="unidad in unidades" :key="unidad.id" :value="unidad.id">{{ unidad.nombre_unidad }}</option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_unidad_solicitante">{{ errors.id_unidad_solicitante[0] }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Tipo de Contratación *</label>
              <select v-model="form.tipo_contratacion" class="form-select" :class="{ 'is-invalid': errors.tipo_contratacion }" required>
                <option value="">Seleccionar tipo</option>
                <option value="BIENES">Bienes</option>
                <option value="SERVICIOS">Servicios</option>
                <option value="CONSULTORIA_LINEA">Consultoría de Línea</option>
                <option value="CONSULTORIA_PRODUCTO">Consultoría por Producto</option>
              </select>
              <div class="invalid-feedback" v-if="errors.tipo_contratacion">{{ errors.tipo_contratacion[0] }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Método de Selección</label>
              <select v-model="form.metodo_seleccion" class="form-select" :class="{ 'is-invalid': errors.metodo_seleccion }" required>
                <option value="">Seleccionar Metodo</option>
                <option value="Calidad">Calidad</option>
                <option value="Presupuesto Fijo">Presupuesto Fijo</option>
                <option value="Menor Costo">Menor Costo</option>
                <option value="Precio Evaluado Más Bajo">Precio Evaluado Más Bajo</option>
              </select>
              <div class="invalid-feedback" v-if="errors.tipo_contratacion">{{ errors.tipo_contratacion[0] }}</div>
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="form-label">Objeto de la Contratación *</label>
            </div>
          </div>
          <div class="col-md-10">
            <div class="form-group mb-2">
              <input v-model="form.objeto" type="text" class="form-control" :class="{ 'is-invalid': errors.objeto }" required>
              <div class="invalid-feedback" v-if="errors.objeto">{{ errors.objeto[0] }}</div>
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="form-label">Justificación</label>
            </div>
          </div>
          <div class="col-md-10">
            <div class="form-group mb-2">
              <textarea v-model="form.justificacion" class="form-control" rows="2" :class="{ 'is-invalid': errors.justificacion }"></textarea>
            </div>
          </div>
          
          <!-- Recurso -->
          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="form-label">Recurso *</label>
            </div>
          </div>
          <div class="col-md-10">
            <div class="form-group mb-2">
              <select v-model="form.id_recurso" class="form-select" :class="{ 'is-invalid': errors.id_recurso }" required>
                <option value="">Seleccionar recurso</option>
                <option v-for="recurso in recursos" :key="recurso.id" :value="recurso.id">
                  {{ recurso.descripcion_recursos }} - 
                  {{ recurso.fuente_financiamiento }} | 
                  {{ recurso.programa_poa }} | 
                  {{ recurso.actividad_poa }}
                  (Bs. {{ formatNumber(recurso.monto) }})
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_recurso">{{ errors.id_recurso[0] }}</div>
            </div>
          </div>
          
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Código PAC</label>
              <input v-model="form.codigo_pac" type="text" class="form-control">
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Código Poa</label>
              <input v-model="form.codigo_poa" type="text" class="form-control">
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="form-label">Plazo Entrega (días)</label>
              <input v-model="form.plazo_entrega_dias" type="number" class="form-control">
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="form-label">Lugar de Entrega</label>
            </div>
          </div>
          <div class="col-md-10">
            <div class="form-group mb-2">
              <input v-model="form.lugar_entrega" type="text" value="DDE-Beni, Calle Nicolás Suárez Esq. Mcal. Sucre s/n, Trinidad, Bolivia" class="form-control">
            </div>
          </div>
        </div>
        
        <div class="alert alert-info mt-2">
          <i class="fa fa-info-circle"></i> 
          Después de crear la solicitud, podrá agregar los ítems (detalles) desde la página de edición.
        </div>
        
        <div class="modal-footer mt-3">
          <BButton type="button" variant="secondary" @click="modalVisible = false">Cancelar</BButton>
          <BButton type="submit" variant="primary" :disabled="loadingForm">
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status"></span>
            Crear Solicitud
          </BButton>
          <!--
          <BButton type="button" variant="success" @click="guardarYEnviar" :disabled="loadingForm">
            Crear y Enviar a Autorización
          </BButton>
          -->
        </div>
      </form>
    </BModal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton, BPagination, BBadge, BModal } from 'bootstrap-vue-3';

const router = useRouter();

// Datos
const solicitudes = ref({ data: [] });
const unidades = ref([]);
const recursos = ref([]);
const estados = ref([]);
const loading = ref(true);
const loadingForm = ref(false);
const modalVisible = ref(false);
const errors = ref({});
const generandoPdf = ref(false);
const editMode = ref(false);
// Filtros
const filters = ref({ search: '', estado: '', tipo_contratacion: '' });
const pagination = ref({ current_page: 1, per_page: 10, total: 0 });

// Formulario
const form = ref({
  id_unidad_solicitante: '',
  tipo_contratacion: '',
  objeto: '',
  justificacion: '',
  id_recurso: '',
  codigo_pac: '',
  codigo_poa: '',
  metodo_seleccion: '',
  plazo_entrega_dias: '',
  lugar_entrega: 'DDE-Beni, Calle Nicolás Suárez Esq. Mcal. Sucre s/n, Trinidad, Bolivia'
});

// Fetch data
const fetchSolicitudes = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/solicitud-contratacion/listar', {
      params: { 
        page: pagination.value.current_page, 
        per_page: pagination.value.per_page, 
        ...filters.value 
      }
    });
    solicitudes.value = response.data.data;
    pagination.value.total = response.data.data.total;
  } catch (error) { 
    console.error('Error fetching solicitudes:', error);
    Swal.fire('Error', 'No se pudieron cargar las solicitudes', 'error');
  } finally { 
    loading.value = false; 
  }
};

const fetchUnidades = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/unidades');
    unidades.value = response.data.data;
  } catch (error) { 
    console.error('Error fetching unidades:', error); 
  }
};

const fetchRecursos = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/recursos');
    recursos.value = response.data.data;
  } catch (error) { 
    console.error('Error fetching recursos:', error); 
  }
};

const fetchEstados = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/estados');
    estados.value = response.data.data;
  } catch (error) { 
    console.error('Error fetching estados:', error); 
  }
};

// Modal functions
const openModal = () => {
  errors.value = {};
  form.value = {
    id_unidad_solicitante: '',
    tipo_contratacion: '',
    objeto: '',
    justificacion: '',
    id_recurso: '',
    codigo_pac: '',
    codigo_poa: '',
    metodo_seleccion: '',
    plazo_entrega_dias: '',
    lugar_entrega: 'DDE-Beni, Calle Nicolás Suárez Esq. Mcal. Sucre s/n, Trinidad, Bolivia'
  };
  modalVisible.value = true;
};

const handleSubmit = async () => {
  loadingForm.value = true;
  errors.value = {};
  
  try {
    let response;
    if (form.value.id) {
      // Actualizar solicitud existente
      response = await axios.put(`/solicitud-contratacion/actualizar/${form.value.id}`, form.value);
      Swal.fire({
        title: 'Éxito',
        text: response.data.message || 'Solicitud actualizada correctamente',
        icon: 'success',
        confirmButtonText: 'OK'
      }).then(() => {
        modalVisible.value = false;
        fetchSolicitudes();
      });
    } else {
      // Crear nueva solicitud
      response = await axios.post('/solicitud-contratacion/guardar', form.value);
      Swal.fire({
        title: 'Éxito',
        text: response.data.message,
        icon: 'success',
        confirmButtonText: 'OK'
      }).then(() => {
        modalVisible.value = false;
        // Redirigir a la edición para agregar detalles
        fetchSolicitudes();
        // router.push({ name: 'solicitud-contratacion-edit', params: { id: response.data.data.id } });
      });
    }
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors;
    } else {
      Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
    }
  } finally { 
    loadingForm.value = false; 
  }
};

const guardarYEnviar = async () => {
  loadingForm.value = true;
  errors.value = {};
  try {
    const response = await axios.post('/solicitud-contratacion/guardar', form.value);
    await axios.post(`/solicitud-contratacion/enviar-autorizacion/${response.data.data.id}`);
    Swal.fire({
      title: 'Éxito',
      text: 'Solicitud creada y enviada a autorización',
      icon: 'success',
      confirmButtonText: 'OK'
    }).then(() => {
      modalVisible.value = false;
      fetchSolicitudes();
    });
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors;
    } else {
      Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
    }
  } finally { 
    loadingForm.value = false; 
  }
};
// Obtener datos de una solicitud para editar
const fetchSolicitudData = async (id) => {
  try {
    const response = await axios.get(`/solicitud-contratacion/ver/${id}`);
    const data = response.data.data;
    form.value = {
      id: data.id,
      id_unidad_solicitante: data.id_unidad_solicitante,
      tipo_contratacion: data.tipo_contratacion,
      objeto: data.objeto,
      justificacion: data.justificacion || '',
      id_recurso: data.id_recurso,
      codigo_pac: data.codigo_pac || '',
      codigo_poa: data.codigo_poa || '',
      metodo_seleccion: data.metodo_seleccion || '',
      plazo_entrega_dias: data.plazo_entrega_dias || '',
      lugar_entrega: data.lugar_entrega || ''
    };
  } catch (error) {
    console.error('Error fetching solicitud data:', error);
    Swal.fire('Error', 'No se pudieron cargar los datos de la solicitud', 'error');
  }
};
const openEditModal = async (solicitud) => {
  editMode.value = true;
  errors.value = {};
  await fetchSolicitudData(solicitud.id);
  modalVisible.value = true;
};
// Navegación
const editarSolicitud = (id) => {
  router.push({ name: 'solicitud-contratacion-edit', params: { id } });
};

const showDetails = (id) => { 
  router.push({ name: 'solicitud-contratacion-show', params: { id } }); 
};

const showDetails2 = (id) => { 
  router.push({ name: 'solicitud-contratacion-detalle', params: { id } }); 
};

const enviarAutorizacion = async (solicitud) => {
  const result = await Swal.fire({
    title: '¿Enviar a autorización?',
    text: `La solicitud ${solicitud.codigo} será enviada al RPA para autorización de inicio.`,
    icon: 'question', 
    showCancelButton: true, 
    confirmButtonText: 'Sí, enviar'
  });
  
  if (result.isConfirmed) {
    try {
      await axios.post(`/solicitud-contratacion/enviar-autorizacion/${solicitud.id}`);
      Swal.fire('Éxito', 'Solicitud enviada a autorización', 'success');
      fetchSolicitudes();
    } catch (error) { 
      Swal.fire('Error', error.response?.data?.message || 'Error al enviar', 'error'); 
    }
  }
};

const confirmDelete = (solicitud) => {
  Swal.fire({
    title: '¿Estás seguro?', 
    text: `Eliminará la solicitud ${solicitud.codigo}`,
    icon: 'warning', 
    showCancelButton: true, 
    confirmButtonText: 'Sí, eliminar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await axios.delete(`/solicitud-contratacion/eliminar/${solicitud.id}`);
        Swal.fire('Éxito', 'Solicitud eliminada', 'success');
        fetchSolicitudes();
      } catch (error) { 
        Swal.fire('Error', error.response?.data?.message || 'Error al eliminar', 'error'); 
      }
    }
  });
};
/**
 * Generar PDF del Formulario Único de Solicitud (FUS)
 */
const generarPdfFus = async (solicitud) => {
  generandoPdf.value = true;
  try {
    const response = await axios.get(`/solicitud-contratacion/pdf/fus/${solicitud.id}`, {
      responseType: 'blob'
    });
    
    // Crear URL del blob y descargar
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const nombreArchivo = `FUS_${solicitud.codigo}.pdf`;
    
    link.href = blobUrl;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    Swal.fire('Éxito', 'Formulario Único de Solicitud generado correctamente', 'success');
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire('Error', error.response?.data?.message || 'No se pudo generar el PDF', 'error');
  } finally {
    generandoPdf.value = false;
  }
};

// Permisos basados en estado
const canEdit = (s) => ['BORRADOR', 'RECHAZADA'].includes(s.estado);
const canSend = (s) => s.estado === 'BORRADOR';
const canDelete = (s) => s.estado === 'BORRADOR';

// Funciones de filtrado y paginación
const handleSearch = () => { 
  pagination.value.current_page = 1; 
  fetchSolicitudes(); 
};

const handlePageChange = (page) => { 
  pagination.value.current_page = page; 
  fetchSolicitudes(); 
};

// Formateadores
const formatDate = (date) => date ? new Date(date).toLocaleDateString('es-BO') : 'N/A';
const formatNumber = (n) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2 }).format(n || 0);
const formatTipo = (t) => ({ 
  'BIENES':'Bienes',
  'SERVICIOS':'Servicios', 
  'CONSULTORIA_LINEA':'Consultoría Línea',
  'CONSULTORIA_PRODUCTO':'Consultoría Producto'
}[t] || t);
const formatEstado = (e) => e?.replace(/_/g, ' ').toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'N/A';
const truncate = (t, l) => t?.length > l ? t.substring(0, l) + '...' : t;

const getTipoVariant = (t) => ({ 
  'BIENES':'primary',
  'SERVICIOS':'info',
  'CONSULTORIA_LINEA':'success',
  'CONSULTORIA_PRODUCTO':'warning'
}[t] || 'secondary');

const getEstadoVariant = (e) => ({ 
  'BORRADOR':'secondary',
  'EN_AUTORIZACION_RPA':'warning',
  'AUTORIZADA':'success',
  'RECHAZADA':'danger',
  'EN_CERTIFICACION':'info',
  'CERTIFICADA':'info',
  'EN_SELECCION':'primary',
  'EN_ADJUDICACION':'warning',
  'ADJUDICADA':'success',
  'EN_CONTRATACION':'primary',
  'CONTRATADA':'success',
  'EN_RECEPCION':'warning',
  'RECIBIDA':'dark',
  'CONCLUIDA':'light'
}[e] || 'secondary');

const exportPDF = async () => {
  try {
    const response = await axios.get('/solicitud-contratacion/reporte', { 
      params: filters.value, 
      responseType: 'blob' 
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `solicitudes-${new Date().toISOString().split('T')[0]}.pdf`);
    link.click();
    link.remove();
  } catch (error) { 
    console.error('Error exporting PDF:', error);
    Swal.fire('Error', 'No se pudo generar el PDF', 'error'); 
  }
};

onMounted(() => { 
  fetchSolicitudes(); 
  fetchUnidades(); 
  fetchRecursos();
  fetchEstados(); 
});
</script>

<style scoped>
.card-header { background-color: #DBECDB; }
.table-responsive { max-height: 600px; overflow-y: auto; }
thead th { 
  background-color: #D84315; 
  color: #fff; 
  position: sticky; 
  top: 0; 
  z-index: 10; 
}
.btn-round { border-radius: 50px; }
</style>